import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import fetchAiCostFilterOptions from '@Application/AiCosts/fetchAiCostFilterOptions';
import FetchAiCostFilterOptions from '@Application/AiCosts/Queries/FetchAiCostFilterOptions';
import FacilityFilter from '@UI/Pages/Administration/AiCosts/Filters/FacilityFilter';
import OrganizationFilter from '@UI/Pages/Administration/AiCosts/Filters/OrganizationFilter';
import PeriodFilter from '@UI/Pages/Administration/AiCosts/Filters/PeriodFilter';
import PeriodPresets from '@UI/Pages/Administration/AiCosts/Filters/PeriodPresets';
import ScopeReminder from '@UI/Pages/Administration/AiCosts/Filters/ScopeReminder';
import aiCostsFiltersSignal from '@UI/Pages/Administration/AiCosts/Filters/Signals/aiCostsFiltersSignal';
import UserFilter from '@UI/Pages/Administration/AiCosts/Filters/UserFilter';
import { css } from '@emotion/react';
import type * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useStoreQuery } from '@verclam/synapse';

const cardStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1.25rem;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.border};
`;

const periodRowStyle = css`
	display: flex;
	flex-wrap: wrap;
	align-items: flex-end;
	gap: 1rem;
`;

const scopeRowStyle = css`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 1rem;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		grid-template-columns: minmax(0, 1fr);
	}
`;

type Props = { accountId: number };

/**
 * The filter card: period (shortcuts and range) on every tab, structure / facility / user on the
 * subject tabs only. The lists of those three come from one read, shared by the cascade.
 */
const Filters: React.FC<Props> = ({ accountId }) => {
	const dispatch = useDispatch();
	const filters = useSignalValue(aiCostsFiltersSignal);

	const { data: options } = useStoreQuery({
		selector: (state) => state.aiCostsStore.filterOptions,
		stopOn: (value) => value !== null && value.accountId === accountId,
		query: () => dispatch(fetchAiCostFilterOptions(new FetchAiCostFilterOptions(accountId))),
		deps: [accountId],
	});
	const currentOptions = options !== null && options.accountId === accountId ? options : null;

	return (
		<div css={cardStyle}>
			<div css={periodRowStyle}>
				<PeriodPresets />
				<PeriodFilter />
			</div>
			<ConditionalLoader data={filters.isSubjectTab}>
				<div css={scopeRowStyle}>
					<OrganizationFilter options={currentOptions} />
					<FacilityFilter options={currentOptions} />
					<UserFilter options={currentOptions} />
				</div>
			</ConditionalLoader>
			<ScopeReminder options={currentOptions} />
		</div>
	);
};

export default Filters;
